// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Section, SectionRail } from './primitives';
import BillingBoundary, { BoundaryChild } from './BillingBoundary';
import {
  getBillingSummary,
  getConnectStatus,
  createOnboardingLink,
  regenerateOnboardingLink,
} from '../../../api/billingApi';
import './billing.css';

/* Tenant Billing — /settings/billing.
   Data of record: GET /api/billing/summary/ (connect + keys/provenance +
   surfaces + payments) with GET /api/billing/connect/status/ for the live
   onboarding detail, and POST /api/billing/connect/onboarding-link/ for the
   single-use, ~10-min, login-free hosted link the founder reads on a call.
   Built on settings-v2 primitives + tokens (light + dark). */

const SECTIONS = [
  { id: 'status', label: 'Payments' },
  { id: 'link', label: 'Onboarding link' },
  { id: 'keys', label: 'Keys & provenance' },
  { id: 'surfaces', label: 'Pay surfaces' },
  { id: 'payments', label: 'Recent payments' },
];

const STATUS_LABEL = { not_started: 'Not started', onboarding: 'Onboarding', active: 'Active' };
const STATUS_CLASS = { not_started: 'off', onboarding: 'info', active: 'ok' };

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(typeof v === 'number' ? v * 1000 : v).toLocaleString(); }
  catch { return String(v); }
};
const fmtMoney = (amount, currency = 'USD') => {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  if (isNaN(n)) return String(amount);
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'USD').toUpperCase() }).format(n); }
  catch { return `$${n.toFixed(2)}`; }
};
const mmss = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const surfaceOn = (list, key) =>
  (list || []).some((s) => String(s).toLowerCase().includes(key));

// Stripe's requirements can arrive as plain strings ("individual.id_number")
// OR as rich objects ({errors, impact, reference, description, …}). Rendering
// the object directly throws React #31 and blanks the page — always coerce to
// a human-readable string.
const reqLabel = (r) => {
  if (r == null) return '';
  if (typeof r === 'string') return r;
  if (typeof r === 'object') {
    return r.description || r.requirement || r.reference || r.code || 'Additional information required';
  }
  return String(r);
};
// disabled_reason can be a string or an object — never render it raw.
const asText = (v) => {
  if (v == null || v === '') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.description || v.reason || v.message || JSON.stringify(v);
  return String(v);
};

function useCountdown(link) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!link) { setRemaining(0); return undefined; }
    const t0 = link.receivedAt;
    const compute = () => Math.max(0, link.expires_in_seconds - (Date.now() / 1000 - t0));
    setRemaining(compute());
    const id = setInterval(() => {
      const r = compute(); setRemaining(r);
      if (r <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [link]);
  return remaining;
}

function Flag({ on, label }) {
  return (
    <span className={`bl-flag ${on ? 'on' : 'off'}`}>
      <span className="bl-flag-dot" aria-hidden />{label}
    </span>
  );
}

// ── Design preview (?demo=1) — renders the full surface incl. a ticking
// countdown with no auth. Loudly badged; never a fallback over live data.
const DEMO = (() => { try { return new URLSearchParams(window.location.search).get('demo') === '1'; } catch { return false; } })();
const NOW = Date.now() / 1000;
const DEMO_SUMMARY = {
  tenant_id: 27, tenant_name: 'RT’s Ranch and Event', fieldroutes_billed: false,
  connect: { status: 'onboarding', account_id: 'acct_1AbCdEfGhIjKlMnO', charges_enabled: false, payouts_enabled: false, details_submitted: true, onboarding_status: 'in_progress' },
  keys: { key1_allowlisted: true, key2_verified: true, key2_provenance: { by: 'khalis@abon.ai', at: '2026-09-04T18:22:00Z', note: 'Founder-verified live key' }, billing_live: true, is_test_tenant: false },
  surfaces: { billing_enabled: true, allowed_payment_surfaces: ['portal', 'crm'] },
  payments: { recent: [
    { id: 1, amount: '285.00', currency: 'USD', status: 'succeeded', created_at: '2026-09-04T15:22:00Z', method: 'card', category: 'invoice', failure: null, customer: 'Maria Delgado' },
    { id: 2, amount: '1240.00', currency: 'USD', status: 'succeeded', created_at: '2026-09-04T11:08:00Z', method: 'card', category: 'deposit', failure: null, customer: 'Northgate HOA' },
    { id: 3, amount: '95.00', currency: 'USD', status: 'failed', created_at: '2026-09-03T18:44:00Z', method: 'card', category: 'invoice', failure: 'Card declined (insufficient_funds)', customer: 'Tori Vance' },
  ], counts: { succeeded: 2, failed: 1 } },
};
const DEMO_LINK = { url: 'https://connect.stripe.com/setup/e/acct_1AbCdEfGhIjKlMnO/aB12cD9xYzQ', expires_in_seconds: 600, expires_at: NOW + 587, server_time: NOW, account_id: 'acct_1AbCdEfGhIjKlMnO', onboarding_status: 'pending', livemode: true, prefilled_email: 'tori@venue.example', action: 'created', receivedAt: NOW - 13 };
const DEMO_CONNECTLIVE = { can_accept_payments: false, requirements_due: ['identity.individual.id_number'], disabled_reason: null, eligible: true };

// A settings Section wrapped in its own error boundary, so one card's render
// failure degrades to an inline "unavailable — retry" while the others render.
// `children` is a render FUNCTION (see BoundaryChild) so the card body renders
// as a child of the boundary and a throw stays isolated to this card.
function Card({ boundaryTitle, onRetry, title, children, ...rest }) {
  return (
    <BillingBoundary title={boundaryTitle || `${title} unavailable`} onRetry={onRetry}>
      <Section title={title} {...rest}>
        <BoundaryChild render={children} />
      </Section>
    </BillingBoundary>
  );
}

function BillingTabInner() {
  const [summary, setSummary] = useState(DEMO ? DEMO_SUMMARY : null);
  const [loading, setLoading] = useState(!DEMO);
  const [loadError, setLoadError] = useState(null);
  const [connectLive, setConnectLive] = useState(DEMO ? DEMO_CONNECTLIVE : null);

  const [email, setEmail] = useState('');
  const [link, setLink] = useState(DEMO ? DEMO_LINK : null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const remaining = useCountdown(link);
  const linkExpired = link && remaining <= 0;
  const pollRef = useRef(null);
  const container = useRef(null);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await getBillingSummary();
      setSummary(data); setLoadError(null);
      return data;
    } catch (e) {
      setLoadError(e?.status === 403
        ? 'You need an admin or manager role to manage payments.'
        : "Couldn't load billing.");
      return null;
    } finally { setLoading(false); }
  }, []);

  const loadConnectLive = useCallback(async (refresh = false) => {
    try { const { data } = await getConnectStatus(refresh ? { refresh: 1 } : {}); setConnectLive(data); }
    catch { /* best-effort detail */ }
  }, []);

  // A card boundary's Retry re-pulls both payloads so a transient 500 or a
  // malformed status response can recover without a full page reload.
  const retry = useCallback(() => { loadSummary(); loadConnectLive(true); }, [loadSummary, loadConnectLive]);

  useEffect(() => { if (!DEMO) loadSummary(); }, [loadSummary]);

  const connect = summary?.connect || {};
  const keys = summary?.keys || {};
  const surfaces = summary?.surfaces || {};
  const payments = summary?.payments?.recent || [];
  const fieldRoutes = !!summary?.fieldroutes_billed;
  const allowlisted = !!keys.key1_allowlisted;
  const status = connect.status || 'not_started';
  const canAccept = status === 'active' || !!connectLive?.can_accept_payments;
  const showLink = !fieldRoutes && allowlisted && !canAccept;

  // Poll while a link is live or onboarding is mid-flow.
  useEffect(() => {
    const shouldPoll = !DEMO && !canAccept && !fieldRoutes && allowlisted &&
      (((!!link && !linkExpired)) || status === 'onboarding');
    if (!shouldPoll) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return undefined; }
    loadConnectLive(false);
    pollRef.current = setInterval(() => { loadSummary(); loadConnectLive(false); }, 8000);
    const onFocus = () => { loadSummary(); loadConnectLive(true); };
    window.addEventListener('focus', onFocus);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      window.removeEventListener('focus', onFocus);
    };
  }, [canAccept, fieldRoutes, allowlisted, link, linkExpired, status, loadSummary, loadConnectLive]);

  const mapLinkError = (e) => {
    const code = e?.payload?.code || e?.code;
    if (e?.status === 403 && code === 'not_live_eligible') return "Live payments aren't enabled for this account yet.";
    if (e?.status === 403) return 'You need an admin or manager role to do this.';
    if (e?.status === 409 || code === 'fieldroutes_tenant') return 'Billing is handled in FieldRoutes for this tenant.';
    if (e?.status === 503 || code === 'not_configured') return "Payments setup isn't available right now.";
    if (e?.status === 502) return "Couldn't reach Stripe — try Regenerate.";
    return e?.message || "Couldn't create the onboarding link.";
  };

  const mint = useCallback(async (regenerate) => {
    setLinkBusy(true); setLinkError(null);
    try {
      // Explicit owner click: the backend refuses account creation without confirm.
      const body = email ? { email, confirm: true } : { confirm: true };
      const fn = regenerate ? regenerateOnboardingLink : createOnboardingLink;
      const { data } = await fn(body);
      setLink({ ...data, action: regenerate ? 'regenerated' : (data.action || 'created'), receivedAt: Date.now() / 1000 });
      loadSummary(); loadConnectLive(false);
    } catch (e) { setLinkError(mapLinkError(e)); }
    finally { setLinkBusy(false); }
  }, [email, loadSummary, loadConnectLive]);

  const copyLink = useCallback(async () => {
    if (!link?.url || linkExpired) return;
    try { await navigator.clipboard.writeText(link.url); toast.success('Link copied'); }
    catch { toast.error('Copy failed — select the link manually'); }
  }, [link, linkExpired]);

  const prov = keys.key2_provenance;

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} containerRef={container} />
      <div className="sv2-col" ref={container}>

        {DEMO && (
          <div className="bl-note info" style={{ fontWeight: 600 }}>
            PREVIEW — sample data, no live account. Real status, link and payments load without <code>?demo=1</code>.
          </div>
        )}

        {/* ── Payments status ── */}
        <Card onRetry={retry} boundaryTitle="Stripe status unavailable" id="status" title="Payments"
          sub="Accept card & bank payments through Stripe — set up once, right here.">
          {() => (loading && !summary ? (
            <div className="bl-muted">Checking payment status…</div>
          ) : loadError ? (
            <div className="bl-note danger">{loadError}</div>
          ) : fieldRoutes ? (
            <div className="sv2-obj">
              <div className="sv2-obj-head">
                <span className="sv2-obj-title">Billing handled in FieldRoutes</span>
                <span className="sv2-pill info">FieldRoutes</span>
              </div>
              <div className="sv2-obj-sub" style={{ whiteSpace: 'normal' }}>
                Payments for this tenant are managed in FieldRoutes — nothing to set up here.
              </div>
            </div>
          ) : !allowlisted ? (
            <div className="sv2-obj">
              <div className="sv2-obj-head">
                <span className="sv2-obj-title">Live payments aren’t enabled yet</span>
                <span className="sv2-pill off">Not allow-listed</span>
              </div>
              <div className="sv2-obj-sub" style={{ whiteSpace: 'normal' }}>
                This account isn’t allow-listed for live payments yet (Key 1).
              </div>
            </div>
          ) : (
            <div className="sv2-obj bl-status">
              <div className="sv2-obj-head">
                <span className="sv2-obj-title">{canAccept ? 'Ready to accept payments' : 'Stripe Connect'}</span>
                <span className={`sv2-pill ${STATUS_CLASS[status] || 'off'}`}>{STATUS_LABEL[status] || 'Not started'}</span>
              </div>
              <div className="bl-flags">
                <Flag on={!!connect.charges_enabled} label="Charges" />
                <Flag on={!!connect.payouts_enabled} label="Payouts" />
                <Flag on={!!connect.details_submitted} label="Details submitted" />
              </div>
              {Array.isArray(connectLive?.requirements_due) && connectLive.requirements_due.length > 0 && (
                <div className="bl-req">
                  <span className="bl-req-label">Still needs</span>
                  <ul>{connectLive.requirements_due.map((r, i) => <li key={i}>{reqLabel(r)}</li>)}</ul>
                </div>
              )}
              {connectLive?.disabled_reason && (
                <div className="bl-note danger">Stripe disabled this account: {asText(connectLive.disabled_reason)}</div>
              )}
            </div>
          ))}
        </Card>

        {/* ── Onboarding link ── */}
        {showLink && (
          <Card onRetry={retry} id="link" title="Onboarding link"
            sub="Generate a single-use link, read or send it to the owner, and watch it complete. Expires in ~10 minutes.">
            {() => (<div className="sv2-obj">
              <label className="bl-field">
                <span>Owner email (optional — prefills Stripe)</span>
                <input type="email" placeholder="owner@venue.example" value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>

              {!link ? (
                <div className="sv2-actions">
                  <button className="sv2-btn primary" disabled={linkBusy} onClick={() => mint(false)}>
                    {linkBusy ? 'Generating…' : 'Set up payments'}
                  </button>
                </div>
              ) : (
                <div className={`bl-link ${linkExpired ? 'expired' : ''}`}>
                  <div className="bl-link-top">
                    <span className={`bl-count ${linkExpired ? 'expired' : remaining < 60 ? 'urgent' : ''}`}>
                      {linkExpired ? 'Link expired' : `Expires in ${mmss(remaining)}`}
                    </span>
                    {link.livemode === false && <span className="sv2-pill warn">TEST</span>}
                    {link.action === 'regenerated' && !linkExpired && <span className="sv2-pill info">Reissued</span>}
                  </div>
                  <div className="bl-link-url" title={link.url}>{link.url}</div>
                  <div className="sv2-actions">
                    <button className="sv2-btn" disabled={linkExpired} onClick={copyLink}>Copy link</button>
                    <a className={`sv2-btn ${linkExpired ? 'is-disabled' : ''}`} href={link.url} target="_blank" rel="noreferrer"
                      onClick={(e) => { if (linkExpired) e.preventDefault(); }} aria-disabled={linkExpired}>Open</a>
                    <button className="sv2-btn primary" disabled={linkBusy} onClick={() => mint(true)}>
                      {linkBusy ? 'Reissuing…' : 'Regenerate'}
                    </button>
                  </div>
                  {status === 'onboarding' && !linkExpired && (
                    <div className="bl-muted bl-poll">Waiting for the owner to finish Stripe’s steps… <span className="bl-dotpulse" /></div>
                  )}
                </div>
              )}
              {linkError && <div className="bl-note danger">{linkError}</div>}
            </div>)}
          </Card>
        )}

        {/* ── Keys & provenance (real) ── */}
        <Card onRetry={retry} id="keys" title="Keys & provenance"
          sub="Which Stripe key this tenant runs on, and who verified it.">
          {() => (<div className="sv2-obj">
            <div className="bl-kv"><span>Allow-listed (Key 1)</span>
              <span className={`sv2-pill ${keys.key1_allowlisted ? 'ok' : 'off'}`}>{keys.key1_allowlisted ? 'Yes' : 'No'}</span>
            </div>
            <div className="bl-kv"><span>Verified (Key 2)</span>
              <span className={`sv2-pill ${keys.key2_verified ? 'ok' : 'off'}`}>{keys.key2_verified ? 'Verified' : 'Not verified'}</span>
            </div>
            {prov && (prov.by || prov.at) && (
              <div className="bl-kv"><span>Verified by</span>
                <span className="bl-mono">{prov.by || '—'}{prov.at ? ` · ${fmtDate(prov.at)}` : ''}</span>
              </div>
            )}
            <div className="bl-kv"><span>Mode</span>
              <span className={`sv2-pill ${keys.is_test_tenant ? 'warn' : keys.billing_live ? 'ok' : 'off'}`}>
                {keys.is_test_tenant ? 'Test' : keys.billing_live ? 'Live' : 'Live — not collecting yet'}
              </span>
            </div>
            <div className="bl-kv"><span>Connected account</span>
              <span className="bl-mono">{connect.account_id || '—'}</span>
            </div>
          </div>)}
        </Card>

        {/* ── Pay surfaces (real) ── */}
        <Card onRetry={retry} id="surfaces" title="Pay surfaces" sub="Where customers can pay this tenant.">
          {() => (<div className="sv2-obj">
            <div className="bl-kv"><span>Payments enabled</span>
              <span className={`sv2-pill ${surfaces.billing_enabled ? 'ok' : 'off'}`}>{surfaces.billing_enabled ? 'On' : 'Off'}</span>
            </div>
            <div className="bl-kv"><span>Enabled surfaces</span>
              <span className="bl-flags">
                <Flag on={surfaceOn(surfaces.allowed_payment_surfaces, 'portal')} label="Portal" />
                <Flag on={surfaceOn(surfaces.allowed_payment_surfaces, 'crm')} label="CRM" />
                <Flag on={surfaceOn(surfaces.allowed_payment_surfaces, 'mobile')} label="Mobile" />
              </span>
            </div>
          </div>)}
        </Card>

        {/* ── Recent payments (real) ── */}
        <Card onRetry={retry} id="payments" title="Recent payments" sub="Latest payments and any failures.">
          {() => (loading && !summary ? (
            <div className="bl-muted">Loading payments…</div>
          ) : payments.length === 0 ? (
            <div className="bl-muted">No payments yet.</div>
          ) : (
            <div className="bl-table-wrap">
              <table className="bl-table">
                <thead><tr><th>Date</th><th>Customer</th><th className="num">Amount</th><th>Status</th></tr></thead>
                <tbody>
                  {payments.map((p, i) => {
                    const st = String(p.status || '').toLowerCase();
                    const failed = /fail|declin|error/.test(st) || !!p.failure;
                    return (
                      <tr key={p.id || i}>
                        <td>{fmtDate(p.created_at)}</td>
                        <td className="bl-ellip">{p.customer || p.category || '—'}</td>
                        <td className="num">{fmtMoney(p.amount, p.currency)}</td>
                        <td>
                          <span className={`sv2-pill ${failed ? 'warn' : st ? 'ok' : 'off'}`}>{p.status || '—'}</span>
                          {p.failure && <div className="bl-fail" title={p.failure}>{p.failure}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// Page-level net: anything thrown OUTSIDE the per-card boundaries (the rail,
// the layout) still degrades to a message instead of a blank Settings pane.
// Retry resets the boundary, which re-mounts the inner tab and re-fetches.
export default function BillingTab() {
  return (
    <BillingBoundary title="Billing is unavailable">
      <BillingTabInner />
    </BillingBoundary>
  );
}
